import type { CSSProperties } from 'react';
import type { VariantDef } from '@/engine/types';

/** Face positions of the cube, in value order for numeric dice: 1 front ... 6 back. */
const NUMERIC_POS: Record<number, FacePos> = {
  1: 'front',
  2: 'right',
  3: 'top',
  4: 'bottom',
  5: 'left',
  6: 'back',
};
const LETTER_POS: FacePos[] = [
  'front',
  'right',
  'back',
  'left',
  'top',
  'bottom',
];
type FacePos = 'front' | 'back' | 'right' | 'left' | 'top' | 'bottom';

/** Cube rotation (x, y) that brings a face position to the front. */
const ROTATION: Record<FacePos, [number, number]> = {
  front: [0, 0],
  right: [0, -90],
  top: [-90, 0],
  bottom: [90, 0],
  left: [0, 90],
  back: [0, 180],
};

/** Pip slots (0..8 in a 3x3 grid) lit for each numeric face. */
const PIPS: Record<number, number[]> = {
  1: [4],
  2: [2, 6],
  3: [2, 4, 6],
  4: [0, 2, 6, 8],
  5: [0, 2, 4, 6, 8],
  6: [0, 2, 3, 5, 6, 8],
};

export function facePosFor(value: number, lettered: boolean): FacePos {
  if (lettered) return LETTER_POS[value] ?? 'front';
  return NUMERIC_POS[value] ?? 'front';
}

export function rotationFor(
  value: number,
  lettered: boolean
): [number, number] {
  return ROTATION[facePosFor(value, lettered)];
}

export interface DieCubeProps {
  /** Die value: 1..6 numeric, or face index 0..5 for lettered dice. */
  value: number;
  /** Letters for this die (6), or null for numeric. */
  letters?: string[] | null;
  pipColors?: VariantDef['pipColors'];
  /** Extra full turns accumulated on X and Y so transitions always spin forward. */
  spinX?: number;
  spinY?: number;
  /** Skip the transform transition. */
  instant?: boolean;
}

/** The 3D cube alone (no button chrome). Size comes from --die-size. */
export function DieCube({
  value,
  letters,
  pipColors,
  spinX = 0,
  spinY = 0,
  instant,
}: DieCubeProps) {
  const lettered = !!letters;
  const [rx, ry] = rotationFor(value, lettered);
  const style: CSSProperties = {
    transform: `rotateX(var(--tilt-x, 0deg)) rotateY(var(--tilt-y, 0deg)) rotateX(${rx + spinX * 360}deg) rotateY(${ry + spinY * 360}deg)`,
  };
  const faces: { pos: FacePos; v: number }[] = lettered
    ? LETTER_POS.map((pos, i) => ({ pos, v: i }))
    : [1, 2, 3, 4, 5, 6].map(v => ({ pos: NUMERIC_POS[v]!, v }));

  return (
    <>
      <span className="die-ground" aria-hidden />
      <div
        className="die-cube"
        style={style}
        data-instant={instant ? 'true' : undefined}
      >
        {faces.map(({ pos, v }) =>
          lettered ? (
            <div
              key={pos}
              className="die-face is-letter"
              data-pos={pos}
              aria-hidden
            >
              {letters[v] ?? ''}
            </div>
          ) : (
            <div
              key={pos}
              className="die-face"
              data-pos={pos}
              data-pip={pipColors ? pipColors[v] : undefined}
              aria-hidden
            >
              {Array.from({ length: 9 }, (_, i) => (
                <span
                  key={i}
                  className={PIPS[v]?.includes(i) ? 'pip on' : 'pip'}
                />
              ))}
            </div>
          )
        )}
      </div>
    </>
  );
}

/** Accessible description of a die's showing face. */
export function faceLabel(value: number, letters?: string[] | null): string {
  if (letters) return letters[value] ?? '?';
  return String(value);
}
